/* eslint-disable no-param-reassign */
import { useNavigate } from "react-router-dom";
import { useCallback, useContext, useEffect, useState } from "react";
import { Box, IconButton, SxProps, TableRow, Typography, SpeedDial } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SuccessAlertContext } from "App";
import { checkAccessTokenExpired } from "util/auth";
import ERROR_MESSAGE from "constants/message";
import Notify from "config/notify";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Category,
  SearchApiParams,
  Stock,
  StockSet,
  StockSingle,
  StockSingleInSet,
  Storehouse,
  StorehouseSupplierOptionsWithBlank,
} from "../types";
import useStockList from "../hooks/useStockList";
import useStockSearch from "../hooks/useStockSearch";
import StockSearchDialog from "../components/StockSearchDialog";
import StockTable from "../components/StockTable";
import StockContainer from "../components/StockContainer";
import { categories, supplierBackgroundColor, supplierHeaderColor } from "../constants";
import TableCellSmall, {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "../components/TableCellSmall";
import PlaceSelectDialog from "../components/PlaceSelectDialog";
import {
  checkDisplaySupplier,
  extractNoDupplicationSupplier,
  localeStringDisZero,
  removeEmptyValues,
} from "../utils/common";
import CustomTooltip from "../components/CustomTooltip";

const limit = 10;

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    textAlign: "center",
    p: 0.5,
    backgroundColor: supplierHeaderColor,
    borderBottom: "none",
  },
  supplierBottomCol: {
    textAlign: "center",
    p: 0.5,
    top: 33,
    backgroundColor: supplierHeaderColor,
  },
  topCol: {
    textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  bottomCol: {
    textAlign: "center",
    p: 0.5,
    top: 33,
  },
};

// 隠すことのできるカラム名
const toggleVisibleColumns = [
  "商品コード",
  "JANコード",
  "大分類",
  "仕入先名",
  "ブランド",
  "商品名",
  "品番",
  "サイズ",
];
// 取り除くカラムを設定(タイヤ・ホイールの場合は空)
const generalInvisibleColumnIndices: number[] = [1, 2, 4, 7];
const tyreWheelInvisibleColumnIndices: number[] = [1, 2, 6];
const defaultInvisibleColumnIndices = (c: Category) =>
  c === categories[1] ? generalInvisibleColumnIndices : tyreWheelInvisibleColumnIndices;

// APIに自社倉庫がないため、ハードコーディングで倉庫、物流を定義
const storehouseColumnList: Storehouse[] = [
  { id: "0001", name: "明和町", region: "" },
  { id: "0002", name: "加西", region: "" },
  { id: "0003", name: "伊藤忠", region: "" },
  { id: "0004", name: "昭和製氷", region: "" },
  { id: "0005", name: "東北", region: "" },
  { id: "0006", name: "関東", region: "" },
  { id: "0007", name: "西日本", region: "" },
];

/**
 * 商品部用在庫一覧画面
 * @returns JSX.Element
 */
export default function StockList() {
  const navigate = useNavigate();
  const { showAlert } = useContext(SuccessAlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredStorehouse, setFilteredStorehouse] = useState<Storehouse[]>([]);
  const [
    {
      total,
      stockList,
      isLoading: isStockLoading,
      storehouseUpdated,
      isMoreLoading: isMoreStockLoading,
      isFastLoading,
      fireMoreLoadEvent,
    },
    { get: getStockList, getMore: getMoreStockList, setFireMoreLoadEvent, setInit },
  ] = useStockList(showAlert);
  const [isOpenStockSearch, setIsOpenStockSearch] = useState(true);
  const [isOpenPlaceSelect, setIsOpenPlaceSelect] = useState(false);
  const [
    {
      productCd,
      productName,
      brand,
      JANCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouseSupplierWithBlank,
      storehouse,
      supplier,
      category,
    },
    {
      setProductCd,
      setProductName,
      setBrand,
      setJANCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setStorehouseSupplierWithBlank,
      setStorehouse,
      setSupplier,
      setCategory,
    },
    { classificationOptions, tyreOptions, wheelOptions, storehouseOptions, brandOptions },
    { isLoading: isOptionsLoading },
  ] = useStockSearch(true);
  const [updateStorehouseSupplierWithBlank, setUpdateStorehouseSupplierWithBlank] =
    useState<StorehouseSupplierOptionsWithBlank>("");
  const [updateStorehouse, setUpdateStorehouse] = useState("");
  const [updateSupplier, setUpdateSupplier] = useState("");
  const [invisibleColumnIndices, setInvisibleColumnIndices] = useState<number[]>(
    defaultInvisibleColumnIndices(category),
  );
  const [isScrollTop, setIsScrollTop] = useState(false);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // 単品かセットか
  const checkProductSet = (stock: Stock): stock is StockSet => "productSetCd" in stock;

  // 単品であることは前提で、プルダウンを押したときに表示される、セットに付随する単品かどうか
  const checkProductSingleInSet = useCallback(
    (stock: StockSingle | StockSingleInSet): stock is StockSingleInSet => "quantity" in stock,
    [],
  );

  // 現在の検索条件にあるデータを基にAPIのパラメータを作成
  const createGetListApiParams = (): SearchApiParams => {
    const params: Omit<SearchApiParams, "limit" | "offset"> = {
      item_cd: productCd,
      item_name: productName,
      brand: brand ?? "",
      jan_cd: JANCd,
      item_num: productNumber,
      tire_width: classification === "タイヤ" ? tyreWidth : undefined,
      tire_ratio: classification === "タイヤ" ? tyreRatio : undefined,
      tire_rim_size: classification === "タイヤ" ? tyreRimDiam : undefined,
      wheel_width: classification === "ホイール" ? wheelWidth : undefined,
      wheel_pcd: classification === "ホイール" ? wheelPcd : undefined,
      wheel_hole: classification === "ホイール" ? wheelHole : undefined,
      wheel_rim_size: classification === "ホイール" ? wheelRimDiam : undefined,
      class_cd: classificationOptions.some((co) => co.name === classification)
        ? classificationOptions.filter((co) => co.name === classification)[0].id
        : "",
      vendor_cd: supplier
        ? extractNoDupplicationSupplier(classificationOptions).filter(
            (so) => so.supplierName === supplier,
          )[0].supplierCd
        : "",
    };
    return removeEmptyValues(params);
  };

  // カラムに表示する倉庫、物流を絞り込む
  const filterStorehouse = () => {
    setFilteredStorehouse(storehouseColumnList);
  };

  const onSearchClick = async () => {
    setIsOpenStockSearch(false);
    await getStockList(createGetListApiParams());
    filterStorehouse();
    // 表示するカラムのリセット
    setInvisibleColumnIndices(defaultInvisibleColumnIndices(category));
  };

  const handleToggleOpenColumn = useCallback((index: number) => {
    setInvisibleColumnIndices((prev) => {
      const newIndices = [...prev];
      if (!prev.includes(index)) {
        newIndices.push(index);
        return newIndices;
      }
      return prev;
    });
  }, []);

  const handleVisibleColumn = useCallback((index: number) => {
    setInvisibleColumnIndices((prev) => {
      const newIndices = [...prev];
      newIndices.splice(index, 1);
      return newIndices;
    });
  }, []);

  const handleBack = () => navigate("/");

  const storehouseAmountCell = useCallback(
    (stock: Stock) => {
      let elements: JSX.Element[] = [];
      elements = stock.storehouses.map((sh) => (
        <TableCellNarrow key={sh.id} sx={{ textAlign: "right" }}>
          {sh.amount.toLocaleString()}
        </TableCellNarrow>
      ));
      const diff = filteredStorehouse.length - elements.length;
      if (diff > 0) {
        for (let i = 0; i < diff; i += 1) {
          elements.push(<TableCellNarrow />);
        }
      }
      return elements;
    },
    [filteredStorehouse],
  );

  // 単品のセル
  const clickableSingleCell = useCallback(
    (stock: StockSingle, show: boolean | undefined) =>
      show ? (
        <TableRow key={stock.id}>
          {[
            <TableCellSmall key="product_cd">
              {stock.isDiscontinued ? (
                <CustomTooltip title="*がついているものは廃番です。" arrow>
                  <Box sx={{ cursor: "pointer" }}>
                    <Typography component="span" sx={{ color: "red" }}>
                      *
                    </Typography>
                    {stock.productCd}
                  </Box>
                </CustomTooltip>
              ) : (
                stock.productCd
              )}
            </TableCellSmall>,
            <TableCellSmall key="jan_cd">{stock.JANCd}</TableCellSmall>,
            <TableCellSmall key="classification">
              {classificationOptions.filter((c) => c.id === stock.classification).length > 0
                ? classificationOptions.filter((c) => c.id === stock.classification)[0].name
                : stock.classification}
            </TableCellSmall>,
            <TableCellSmall key="supplier_name" sx={{ backgroundColor: supplierBackgroundColor }}>
              <MultiTypography>{stock.supplierName ?? ""}</MultiTypography>
            </TableCellSmall>,
            <TableCellSmall key="brand">
              <MultiTypography>{stock.brand ?? ""}</MultiTypography>
            </TableCellSmall>,
            <TableCellWide key="product_name">
              <MultiTypography>{stock.productName}</MultiTypography>
            </TableCellWide>,
            <TableCellSmall key="product_number">{stock.productNumber}</TableCellSmall>,
            <TableCellSmall key="size">{stock.size ?? null}</TableCellSmall>,
          ].filter((_, index) => !invisibleColumnIndices.includes(index))}
          {stock.storehouses.length !== 0
            ? storehouseAmountCell(stock)
            : filteredStorehouse.map((sh) => <TableCellNarrow key={sh.id} />)}
          <TableCellSmall sx={{ backgroundColor: supplierBackgroundColor, textAlign: "right" }}>
            {localeStringDisZero(stock.supplierAmount)}
          </TableCellSmall>
          <TableCellNarrow sx={{ backgroundColor: supplierBackgroundColor, maxWidth: 60 }}>
            <MultiTypography>
              {checkDisplaySupplier(stock.supplierDate, stock.supplierAmount)}
            </MultiTypography>
          </TableCellNarrow>
        </TableRow>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      ),
    [storehouseAmountCell, invisibleColumnIndices, checkProductSingleInSet],
  );

  // 混合のセル
  const fieldCell = useCallback(
    (stock: Stock) => {
      let ele: JSX.Element;
      if (checkProductSet(stock)) {
        // セット
        ele = (
          <TableRow key={stock.id}>
            {[
              <TableCellSmall key="product_set_cd">
                <Box display="flex" alignItems="center">
                  {stock.isDiscontinued ? (
                    <CustomTooltip title="*がついているものは廃番です。" arrow>
                      <Box sx={{ cursor: "pointer" }}>
                        <Typography component="span" sx={{ color: "red" }}>
                          *
                        </Typography>
                        {stock.productSetCd}
                      </Box>
                    </CustomTooltip>
                  ) : (
                    stock.productSetCd
                  )}
                </Box>
              </TableCellSmall>,
              <TableCellSmall key="jan_cd">
                {(() => (
                  <>
                    <Box marginBottom={1}>
                      <MultiTypography>{stock.JANCd.split('+')[0]}</MultiTypography>
                    </Box>
                    <Box>
                      <MultiTypography>{stock.JANCd.split('+')[1]}</MultiTypography>
                    </Box>
                  </>
                ))()}
              </TableCellSmall>,
              <TableCellSmall key="classification">
                {classificationOptions.filter((c) => c.id === stock.classification).length > 0
                  ? classificationOptions.filter((c) => c.id === stock.classification)[0].name
                  : stock.classification}
              </TableCellSmall>,
              <TableCellSmall
                key="supplier_name"
                sx={{ backgroundColor: supplierBackgroundColor }}
              />,
              <TableCellSmall key="brand">
                <Box marginBottom={1}>
                  <MultiTypography>{stock.stockSingleList[0].brand}</MultiTypography>
                </Box>
                <Box>
                  <MultiTypography>{stock.stockSingleList[1].brand}</MultiTypography>
                </Box>
              </TableCellSmall>,
              <TableCellWide key="product_name">
                <Box marginBottom={1}>
                  <MultiTypography>{stock.stockSingleList[0].productName}</MultiTypography>
                </Box>
                <Box>
                  <MultiTypography>{stock.stockSingleList[1].productName}</MultiTypography>
                </Box>
              </TableCellWide>,
              <TableCellSmall key="product_number">
                {(() => (
                    <>
                      <Box marginBottom={1}>
                        <MultiTypography>{stock.productNumber.split('+')[0]}</MultiTypography>
                      </Box>
                      <Box>
                        <MultiTypography>{stock.productNumber.split('+')[1]}</MultiTypography>
                      </Box>
                    </>
                ))()}
              </TableCellSmall>,
              <TableCellSmall key="size">
                <Box marginBottom={1}>{stock.stockSingleList[0].size}</Box>
                <Box>{stock.stockSingleList[1].size}</Box>
              </TableCellSmall>,
            ].filter((_, index) => !invisibleColumnIndices.includes(index))}
            {stock.storehouses.length !== 0
              ? storehouseAmountCell(stock)
              : filteredStorehouse.map((sh) => <TableCellNarrow key={sh.id} />)}
            <TableCellSmall sx={{ backgroundColor: supplierBackgroundColor, textAlign: "right" }}>
              -
            </TableCellSmall>
            <TableCellNarrow sx={{ backgroundColor: supplierBackgroundColor, maxWidth: 60 }}>
              -
            </TableCellNarrow>
          </TableRow>
        );
      } else {
        // 単品
        ele = clickableSingleCell(stock, true);
      }
      return ele;
    },
    [storehouseAmountCell, invisibleColumnIndices, clickableSingleCell],
  );

  const iconButton = useCallback(
    (index: number, colName: string) => (
      <Box width="100%">
        <Box width="fit-content" position="relative">
          {colName}
          <Box
            position="absolute"
            top={-20}
            right={10}
            width={0}
            height="fit-content"
            alignContent="center"
          >
            <IconButton onClick={() => handleToggleOpenColumn(index)}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    ),
    [handleToggleOpenColumn],
  );

  const tableHeadElement = useCallback(
    () => (
      <>
        <TableRow>
          {[
            <TableHeaderCellNarrow key="product_cd" rowSpan={2}>
              <MultiTypography>商品コード</MultiTypography>
            </TableHeaderCellNarrow>,
            <TableHeaderCellSmall key="jan_cd" rowSpan={2}>
              {iconButton(1, "JANコード")}
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall key="classification" rowSpan={2}>
              {iconButton(2, "大分類")}
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall
              key="supplier_name"
              rowSpan={2}
              sx={{ backgroundColor: supplierHeaderColor }}
            >
              {iconButton(3, "仕入先名")}
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall key="brand" rowSpan={2}>
              {iconButton(4, "ブランド")}
            </TableHeaderCellSmall>,
            <TableHeaderCellWide key="product_name" rowSpan={2}>
              {iconButton(5, "商品名")}
            </TableHeaderCellWide>,
            <TableHeaderCellSmall key="product_number" rowSpan={2}>
              {iconButton(6, "品番")}
            </TableHeaderCellSmall>,
            <TableHeaderCellSmall key="size" rowSpan={2}>
              {iconButton(7, "サイズ")}
            </TableHeaderCellSmall>,
          ].filter((_, index) => !invisibleColumnIndices.includes(index))}
          {/* <TableHeaderCellSmall>倉庫/仕入先</TableHeaderCellSmall> */}
          <TableHeaderCellNarrow sx={styles.topCol} colSpan={filteredStorehouse.length}>
            <MultiTypography>在庫数量</MultiTypography>
          </TableHeaderCellNarrow>
          <TableHeaderCellSmall colSpan={2} rowSpan={1} sx={styles.supplierTopCol}>
            仕入先
          </TableHeaderCellSmall>
        </TableRow>
        <TableRow>
          {filteredStorehouse.map((sh) => (
            <TableHeaderCellNarrow key={sh.name} sx={styles.bottomCol}>
              <MultiTypography>{`${sh.name}`}</MultiTypography>
            </TableHeaderCellNarrow>
          ))}
          <TableHeaderCellSmall sx={styles.supplierBottomCol}>在庫</TableHeaderCellSmall>
          <TableHeaderCellSmall sx={styles.supplierBottomCol}>更新日時</TableHeaderCellSmall>
        </TableRow>
      </>
    ),
    [filteredStorehouse, invisibleColumnIndices],
  );

  const tableBodyElement = useCallback(
    () => (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>{stockList.length > 0 && stockList.map((stock) => fieldCell(stock))}</>
    ),
    [fieldCell, stockList],
  );

  // スクロール処理
  const handleScroll = async () => {
    setIsScrollTop(window.pageYOffset > 0);

    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current) {
      return;
    }
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  const checkScrollable = () => document.documentElement.scrollHeight > window.innerHeight;

  // 中のコンテンツが小さいとスクロールできず、無限ローディングさせることができないため、事前に+αで読んでおく。
  useEffect(() => {
    if (
      limit <= stockList.length &&
      stockList.length < total &&
      !isLoading &&
      !isFastLoading.current
    ) {
      if (!checkScrollable()) {
        (async () => {
          await getMoreStockList(createGetListApiParams());
        })();
      }
    }
  }, [stockList, isLoading]);

  useEffect(() => {
    setTimeout(() => {
      const userType = localStorage.getItem("user_type");
      if (!(userType === "C0001" || userType === "C0005")) {
        setIsOpenStockSearch(false);
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: "本部ユーザーでログインしてください。再度ログインしてください。",
        });
        setIsOpenStockSearch(false);
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
      } else if (checkAccessTokenExpired()) {
        // 期限切れなら
        setIsOpenStockSearch(false);
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
        });
        setIsOpenStockSearch(false);
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
      }
    }, 0);
  }, []);

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      await getMoreStockList(createGetListApiParams());
    })();
  }, [fireMoreLoadEvent]);

  useEffect(() => {
    (async () => {
      setInit(true);
    })();
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    // loadingの統一
    setIsLoading(isStockLoading || isOptionsLoading);
  }, [isStockLoading, isOptionsLoading]);
  useEffect(() => {
    // 倉庫、物流のfilter初期化
    filterStorehouse();
  }, [isOptionsLoading]);

  return (
    <Box>
      <StockContainer
        stockTitle="在庫情報一覧"
        StockSearch={
          <StockSearchDialog
            open={isOpenStockSearch}
            onClose={() => setIsOpenStockSearch(false)}
            onSearch={onSearchClick}
            productCd={productCd}
            productName={productName}
            brand={brand}
            JANCd={JANCd}
            productNumber={productNumber}
            tyreWidth={tyreWidth}
            tyreRatio={tyreRatio}
            tyreRimDiam={tyreRimDiam}
            wheelWidth={wheelWidth}
            wheelPcd={wheelPcd}
            wheelHole={wheelHole}
            wheelRimDiam={wheelRimDiam}
            classification={classification}
            storehouseSupplier={storehouseSupplierWithBlank}
            storehouse={storehouse}
            supplier={supplier}
            classificationOptions={classificationOptions}
            tyreOptions={tyreOptions}
            wheelOptions={wheelOptions}
            storehouseOptions={storehouseOptions}
            // supplierOptions={supplierOptions}
            brandOptions={brandOptions}
            setProductCd={setProductCd}
            setProductName={setProductName}
            setBrand={setBrand}
            setJANCd={setJANCd}
            setProductNumber={setProductNumber}
            setTyreWidth={setTyreWidth}
            setTyreRatio={setTyreRatio}
            setTyreRimDiam={setTyreRimDiam}
            setWheelWidth={setWheelWidth}
            setWheelPcd={setWheelPcd}
            setWheelHole={setWheelHole}
            setWheelRimDiam={setWheelRimDiam}
            setClassification={setClassification}
            setCategory={setCategory}
            setStorehouseSupplier={setStorehouseSupplierWithBlank}
            setStorehouse={setStorehouse}
            setSupplier={setSupplier}
            isProductStockScreen={false}
          />
        }
        StockTable={
          <StockTable
            searchLen={total}
            tableHeadElement={tableHeadElement}
            tableBodyElement={tableBodyElement}
            isAllLoaded={stockList.length >= total}
            isLoading={isMoreStockLoading}
            toggleVisibleColumns={toggleVisibleColumns}
            invisibleColumnIndices={invisibleColumnIndices}
            handleVisibleColumn={handleVisibleColumn}
            storehouseUpdated={storehouseUpdated}
            isProductStockList
          />
        }
        setIsOpenStockSearch={setIsOpenStockSearch}
        isLoading={isLoading}
        onOpenProductScreen={() => setIsOpenPlaceSelect(true)}
        onBack={handleBack}
        manualName="manual_product"
      />
      <PlaceSelectDialog
        open={isOpenPlaceSelect}
        onClose={() => setIsOpenPlaceSelect(false)}
        onSearch={() =>
          navigate(
            `/stock/main/update?searchName=${updateStorehouse || updateSupplier}&isStorehouse=${updateStorehouseSupplierWithBlank === "倉庫"}&code=${updateStorehouse ? storehouseOptions.filter((so) => so.name === updateStorehouse)[0].id : extractNoDupplicationSupplier(classificationOptions).filter((so) => so.supplierName === updateSupplier)[0].supplierCd}`,
          )
        }
        storehouse={updateStorehouse}
        supplier={updateSupplier}
        storehouseOptions={storehouseOptions}
        supplierOptions={extractNoDupplicationSupplier(classificationOptions)}
        setStorehouseSupplier={setUpdateStorehouseSupplierWithBlank}
        setStorehouse={setUpdateStorehouse}
        setSupplier={setUpdateSupplier}
      />
      {isScrollTop && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <SpeedDial
            ariaLabel="Back to top"
            icon={<KeyboardArrowUpIcon />}
            onClick={scrollToTop}
            open // 常に展開済みにしてアイコンだけ表示
            direction="up" // 展開方向は上だがActionは不要
            FabProps={{ size: "small" }}
          />
        </Box>
      )}
    </Box>
  );
}
